import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Card, Button, Spinner } from "react-bootstrap";
import SettingsService from "services/SettingsService";
import { handleErrors } from "jsx/components/CitoPlus/components/ErrorHandlingComponent";
import { notifySuccess } from "jsx/components/CitoPlus/components/CitoPlusToastr";

const WhatsAppPolicy = ({ onAccepted }) => {
  const [policyText, setPolicyText] = useState("");
  const [policyLoading, setPolicyLoading] = useState(true);

  // Determine whether the policy content is a URL (to show in an iframe)
  const isPolicyUrl = useMemo(() => {
    if (!policyText) return false;
    try {
      const trimmed = policyText.trim();
      const url = new URL(trimmed);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch (err) {
      return false;
    }
  }, [policyText]);

  // Rudimentary HTML detection for when the backend returns HTML content
  const isPolicyHtml = useMemo(() => {
    if (!policyText) return false;
    return /<\/?[a-z][\s\S]*>/i.test(policyText);
  }, [policyText]);

  // Load policy on component mount
  useEffect(() => {
    const loadPolicy = async () => {
      try {
        setPolicyLoading(true);
        const policyData = await SettingsService.getWhatsAppPolicy();
        setPolicyText(policyData.policy_text);
      } catch (error) {
        handleErrors(error);
      } finally {
        setPolicyLoading(false);
      }
    };

    loadPolicy();
  }, []);

  const handleAcceptPolicy = useCallback(async () => {
    try {
      setPolicyLoading(true);
      await SettingsService.acceptWhatsAppPolicy();
      notifySuccess("Política de WhatsApp aceptada exitosamente.");
      if (onAccepted) {
        onAccepted();
      }
    } catch (error) {
      handleErrors(error);
    } finally {
      setPolicyLoading(false);
    }
  }, [onAccepted]);

  if (policyLoading) {
    return (
      <div className="col-lg-12">
        <Card>
          <Card.Header>
            <h4 className="card-title">Política de WhatsApp</h4>
          </Card.Header>
          <Card.Body className="text-center">
            <Spinner animation="border" />
            <p>Cargando política...</p>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div className="col-lg-12">
      <Card>
        <Card.Header>
          <h4 className="card-title">Política de WhatsApp</h4>
        </Card.Header>
        <Card.Body>
          <div
            style={{
              maxHeight: "400px",
              overflowY: "auto",
              marginBottom: "20px",
            }}
          >
            {isPolicyUrl ? (
              <>
                <iframe
                  title="Política de WhatsApp"
                  src={policyText}
                  style={{ width: "100%", height: "400px", border: "none" }}
                  sandbox="allow-forms allow-popups allow-same-origin"
                />
                <p className="mt-2">
                  Si el contenido no se visualiza, puede abrirlo en una nueva
                  pestaña:{" "}
                  <a
                    href={policyText}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {policyText}
                  </a>
                </p>
              </>
            ) : isPolicyHtml ? (
              <div dangerouslySetInnerHTML={{ __html: policyText }} />
            ) : (
              <p>{policyText}</p>
            )}
          </div>
          <div className="text-center">
            <Button
              variant="primary"
              onClick={handleAcceptPolicy}
              disabled={policyLoading}
              size="lg"
            >
              {policyLoading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Aceptando...
                </>
              ) : (
                "Aceptar y Continuar"
              )}
            </Button>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default WhatsAppPolicy;
